/**
 * Meta App Events from inside the Capacitor Android app.
 *
 * In the native app the "pixel" is the Meta (Facebook) Android SDK, not fbevents.js — an
 * app-install campaign attributes installs and in-app events through the SDK only. The SDK lives
 * in the native project; this calls it through a tiny local Capacitor plugin (`MetaEvents`,
 * apps/web/android/app/src/main/java/com/advancehr/hrms/MetaEventsPlugin.java).
 *
 * Every function is a no-op in a browser: the website keeps using the web pixel
 * (lib/meta-pixel.ts), which in turn does not load inside the app, so one action is never counted
 * as both a website and an app event. Nothing personal is sent — just the event name.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

interface MetaEventsPlugin {
  logCompleteRegistration(options: { method: string }): Promise<void>;
  logLogin(): Promise<void>;
}

const MetaEvents = registerPlugin<MetaEventsPlugin>('MetaEvents');

function available(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('MetaEvents');
}

/** A new company signed up from inside the app. Standard event: Complete Registration. */
export function trackAppCompleteRegistration(): void {
  if (!available()) return;
  // Analytics must never break the flow it measures.
  MetaEvents.logCompleteRegistration({ method: 'email' }).catch(() => undefined);
}

/** Someone signed in from inside the app (after MFA, when MFA is on). Custom event: Login. */
export function trackAppLogin(): void {
  if (!available()) return;
  MetaEvents.logLogin().catch(() => undefined);
}
